import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  RefreshCw, Plus, Trash2, Pencil, Loader2, Zap,
  AlertTriangle, CheckCircle, DollarSign, Search,
  Tv, Droplets, Utensils, Car, Shield, Wine, Wrench,
  Play, MoreHorizontal, Lightbulb, ArrowRight, Scan,
  Check, X, TrendingDown
} from "lucide-react";

const CATEGORIES = [
  { value: "cable", label: "Cable/Internet", icon: Tv, color: "#10B981" },
  { value: "electric", label: "Electric", icon: Zap, color: "#F59E0B" },
  { value: "water", label: "Water", icon: Droplets, color: "#3B82F6" },
  { value: "garbage", label: "Garbage", icon: Trash2, color: "#8B5CF6" },
  { value: "food", label: "Food/Groceries", icon: Utensils, color: "#EC4899" },
  { value: "car_payment", label: "Car Payment", icon: Car, color: "#EF4444" },
  { value: "insurance", label: "Insurance", icon: Shield, color: "#14B8A6" },
  { value: "night_out", label: "Night Out", icon: Wine, color: "#F97316" },
  { value: "maintenance", label: "Maintenance", icon: Wrench, color: "#6366F1" },
  { value: "subscription", label: "Subscriptions", icon: Play, color: "#E11D48" },
  { value: "loan_payments", label: "Loan Payments", icon: DollarSign, color: "#78716C" },
  { value: "other", label: "Other", icon: MoreHorizontal, color: "#6B7280" },
];

const getCat = (v) => CATEGORIES.find((c) => c.value === v) || CATEGORIES[11];

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 2,
  });

const FREQ_LABELS = {
  weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly",
  "semi-annual": "Semi-Annual", annual: "Annual",
};

const ConfidenceBar = ({ value }) => {
  const pct = Math.round(value * 100);
  const color = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-zinc-500">{pct}%</span>
    </div>
  );
};

export default function RecurringTransactions() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [detected, setDetected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "", amount: "", category: "subscription", frequency: "monthly", notes: "",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        api.get("/recurring"),
        api.get("/recurring/summary"),
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
    } catch {
      toast.error("Failed to load recurring transactions");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const runDetection = async () => {
    setDetecting(true);
    try {
      const res = await api.post("/recurring/auto-detect");
      setDetected(res.data);
      toast.success(`Found ${res.data.total_detected} recurring patterns`);
    } catch {
      toast.error("Failed to detect patterns");
    } finally {
      setDetecting(false);
    }
  };

  const saveDetected = async (item) => {
    try {
      await api.post("/recurring/save-detected", {
        name: item.name,
        amount: item.avg_amount,
        category: item.category,
        frequency: item.frequency,
        confidence: item.confidence,
        last_date: item.last_date,
      });
      toast.success(`${item.name} saved as recurring`);
      loadData();
      if (detected) {
        setDetected({
          ...detected,
          detected: detected.detected.map((d) =>
            d.name === item.name ? { ...d, already_saved: true } : d
          ),
        });
      }
    } catch {
      toast.error("Failed to save");
    }
  };

  const saveItem = async () => {
    if (!formData.name || !formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Name and positive amount are required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/recurring/${editing.id}`, {
          name: formData.name,
          amount: parseFloat(formData.amount),
          category: formData.category,
          frequency: formData.frequency,
          notes: formData.notes,
        });
        toast.success("Updated");
      } else {
        await api.post("/recurring", {
          name: formData.name,
          amount: parseFloat(formData.amount),
          category: formData.category,
          frequency: formData.frequency,
          notes: formData.notes,
        });
        toast.success("Recurring transaction added");
      }
      setShowAdd(false);
      setEditing(null);
      setFormData({ name: "", amount: "", category: "subscription", frequency: "monthly", notes: "" });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/recurring/${id}`);
      toast.success("Removed");
      loadData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({
      name: item.name, amount: String(item.amount),
      category: item.category, frequency: item.frequency,
      notes: item.notes || "",
    });
    setShowAdd(true);
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/recurring/ai-analyze");
      setAiData(res.data);
      toast.success("AI analysis complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to analyze");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Recurring Transactions">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
        </div>
      </Layout>
    );
  }

  const ratingColors = {
    good: "text-emerald-400 bg-emerald-500/10",
    fair: "text-amber-400 bg-amber-500/10",
    concerning: "text-orange-400 bg-orange-500/10",
    critical: "text-red-400 bg-red-500/10",
  };

  return (
    <Layout title="Recurring Transactions">
      <div className="space-y-6" data-testid="recurring-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Recurring Transactions</h1>
            <p className="text-sm text-zinc-500 mt-1">Track subscriptions, bills & recurring charges</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={runDetection}
              disabled={detecting}
              variant="outline" size="sm"
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 rounded-sm"
              data-testid="auto-detect-btn"
            >
              {detecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Scan className="w-3.5 h-3.5 mr-1.5" />}
              Auto-Detect
            </Button>
            <Button
              onClick={() => { setEditing(null); setFormData({ name: "", amount: "", category: "subscription", frequency: "monthly", notes: "" }); setShowAdd(true); }}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              data-testid="add-recurring-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
            {items.length > 0 && (
              <Button
                onClick={runAi}
                disabled={aiLoading}
                variant="outline" size="sm"
                className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-sm"
                data-testid="ai-analyze-btn"
              >
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5 mr-1.5" />}
                AI Analyze
              </Button>
            )}
            <Button onClick={loadData} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm" data-testid="refresh-recurring-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && summary.total_active > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-active-count">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Active Recurring</p>
                <p className="text-2xl font-bold font-mono text-white mt-1">{summary.total_active}</p>
                <p className="text-xs text-zinc-600 mt-1">tracked items</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-monthly-cost">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Monthly Cost</p>
                <p className="text-2xl font-bold font-mono text-red-400 mt-1">{fmt(summary.monthly_total)}</p>
                <p className="text-xs text-zinc-600 mt-1">all recurring</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-annual-cost">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Annual Cost</p>
                <p className="text-2xl font-bold font-mono text-amber-400 mt-1">{fmt(summary.annual_total)}</p>
                <p className="text-xs text-zinc-600 mt-1">projected yearly</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-top-category">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Top Category</p>
                {summary.by_category?.length > 0 ? (
                  <>
                    <p className="text-2xl font-bold font-mono text-cyan-400 mt-1 capitalize">{getCat(summary.by_category[0].category).label}</p>
                    <p className="text-xs text-zinc-600 mt-1">{fmt(summary.by_category[0].monthly)}/mo</p>
                  </>
                ) : (
                  <p className="text-lg text-zinc-600 mt-1">-</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Saved Recurring Items */}
        {items.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="py-16 text-center">
              <RefreshCw className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">No recurring transactions tracked yet</p>
              <p className="text-zinc-600 text-xs mt-1">Click "Auto-Detect" to scan your expenses or "Add" to create manually</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="recurring-list">
            <h2 className="text-sm text-zinc-500 uppercase tracking-wider">Your Recurring Charges ({items.length})</h2>
            {items.map((item) => {
              const cat = getCat(item.category);
              const Icon = cat.icon;
              return (
                <Card key={item.id} className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid={`recurring-item-${item.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.color}15` }}>
                        <Icon className="w-5 h-5" style={{ color: cat.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white truncate">{item.name}</span>
                          {item.auto_detected && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 rounded-sm">Auto</span>
                          )}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${item.status === "active" ? "bg-emerald-500/10 text-emerald-400" : "bg-zinc-500/10 text-zinc-400"}`}>
                            {item.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-500">
                          <span className="capitalize">{cat.label}</span>
                          <span>{FREQ_LABELS[item.frequency] || item.frequency}</span>
                          {item.confidence && <ConfidenceBar value={item.confidence} />}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-mono text-white">{fmt(item.amount)}</p>
                        <p className="text-[10px] text-zinc-600">/{item.frequency}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button onClick={() => openEdit(item)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-600 hover:text-white" data-testid={`edit-recurring-${item.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button onClick={() => deleteItem(item.id)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-600 hover:text-red-400" data-testid={`delete-recurring-${item.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Auto-Detected Results */}
        {detected && (
          <Card className="bg-[#0A0A0A] border-amber-500/10 rounded-none" data-testid="detection-results">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Scan className="w-4 h-4 text-amber-400" /> Auto-Detected Patterns
                <span className="ml-auto text-xs text-zinc-500 font-normal">
                  {detected.total_detected} found - Est. {fmt(detected.estimated_monthly_recurring)}/mo
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {detected.detected.length === 0 ? (
                <p className="text-sm text-zinc-600 text-center py-4">No recurring patterns detected. Add more expenses to improve detection.</p>
              ) : (
                <div className="space-y-2">
                  {detected.detected.map((d, i) => {
                    const cat = getCat(d.category);
                    const Icon = cat.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 bg-white/[0.02] border border-white/5 p-3 rounded-sm" data-testid={`detected-item-${i}`}>
                        <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.color}15` }}>
                          <Icon className="w-4 h-4" style={{ color: cat.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-white truncate">{d.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-zinc-400 rounded-sm capitalize">{d.frequency}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-500">
                            <span>{d.occurrences}x seen in {d.months_seen} months</span>
                            <ConfidenceBar value={d.confidence} />
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-mono text-white">{fmt(d.avg_amount)}</p>
                          {d.amount_range.min !== d.amount_range.max && (
                            <p className="text-[10px] text-zinc-600">{fmt(d.amount_range.min)}-{fmt(d.amount_range.max)}</p>
                          )}
                        </div>
                        {d.already_saved ? (
                          <span className="text-[10px] px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-sm flex items-center gap-1 flex-shrink-0">
                            <Check className="w-3 h-3" /> Saved
                          </span>
                        ) : (
                          <Button
                            onClick={() => saveDetected(d)}
                            variant="outline" size="sm"
                            className="border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10 rounded-sm text-xs flex-shrink-0"
                            data-testid={`save-detected-${i}`}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Save
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Category Breakdown */}
        {summary && summary.by_category?.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-zinc-400" /> Recurring by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="category-breakdown">
                {summary.by_category.map((c) => {
                  const cat = getCat(c.category);
                  const Icon = cat.icon;
                  const pct = summary.monthly_total > 0 ? (c.monthly / summary.monthly_total) * 100 : 0;
                  return (
                    <div key={c.category} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.color}15` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: cat.color }} />
                      </div>
                      <span className="text-sm text-zinc-300 w-28 truncate">{cat.label}</span>
                      <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: cat.color }} />
                      </div>
                      <span className="text-xs font-mono text-zinc-400 w-20 text-right">{fmt(c.monthly)}/mo</span>
                      <span className="text-[10px] text-zinc-600 w-10 text-right">{pct.toFixed(0)}%</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Analysis */}
        {aiData && (
          <Card className="bg-[#0A0A0A] border-cyan-500/10 rounded-none" data-testid="ai-analysis">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-cyan-400" /> AI Subscription Analysis
                {aiData.health_rating && (
                  <span className={`ml-auto text-xs font-medium px-2.5 py-0.5 rounded-sm capitalize ${ratingColors[aiData.health_rating] || ratingColors.fair}`}>
                    {aiData.health_rating}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {aiData.summary && (
                <p className="text-sm text-zinc-300 leading-relaxed">{aiData.summary}</p>
              )}

              {aiData.potential_savings > 0 && (
                <div className="bg-emerald-500/5 border border-emerald-500/10 p-3 rounded-sm">
                  <p className="text-xs text-emerald-400 uppercase tracking-wider">Potential Monthly Savings</p>
                  <p className="text-xl font-bold font-mono text-emerald-300 mt-1">{fmt(aiData.potential_savings)}</p>
                </div>
              )}

              {aiData.optimization_suggestions?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Optimization Suggestions</p>
                  <div className="space-y-2">
                    {aiData.optimization_suggestions.map((s, i) => (
                      <div key={i} className="bg-white/[0.02] border border-white/5 p-3 rounded-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white font-medium">{s.item}</span>
                          {s.potential_saving > 0 && (
                            <span className="text-xs text-emerald-400 font-mono">Save {fmt(s.potential_saving)}/mo</span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-400">{s.suggestion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiData.keep_items?.length > 0 && (
                  <div>
                    <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Worth Keeping
                    </p>
                    <div className="space-y-1">
                      {aiData.keep_items.map((item, i) => (
                        <p key={i} className="text-xs text-zinc-400 flex items-start gap-1.5">
                          <Check className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" /> {item}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                {aiData.review_items?.length > 0 && (
                  <div>
                    <p className="text-xs text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Review / Consider Canceling
                    </p>
                    <div className="space-y-1">
                      {aiData.review_items.map((item, i) => (
                        <p key={i} className="text-xs text-zinc-400 flex items-start gap-1.5">
                          <TrendingDown className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" /> {item}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {aiData.tips?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Tips</p>
                  <div className="space-y-1.5">
                    {aiData.tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Zap className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                        {tip}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showAdd} onOpenChange={(open) => { setShowAdd(open); if (!open) { setEditing(null); } }}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-md" data-testid="recurring-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">{editing ? "Edit Recurring" : "Add Recurring Transaction"}</DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">Track a subscription or recurring bill</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Netflix, Rent, etc."
                  className="bg-black/50 border-white/10 rounded-sm"
                  data-testid="recurring-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Amount ($)</Label>
                  <Input
                    type="number" min="0.01" step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="15.99"
                    className="bg-black/50 border-white/10 rounded-sm"
                    data-testid="recurring-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Frequency</Label>
                  <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="recurring-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semi-annual">Semi-Annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="recurring-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Notes (optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Payment details..."
                  className="bg-black/50 border-white/10 rounded-sm"
                  data-testid="recurring-notes-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={saveItem} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="save-recurring-btn">
                  {editing ? "Update" : "Add"} Recurring
                </Button>
                <Button onClick={() => { setShowAdd(false); setEditing(null); }} variant="outline" className="border-white/10 text-zinc-400 rounded-sm" data-testid="cancel-recurring-btn">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
